import type { Namespace } from '../types';

// Shared UI-component strings (packages/ui). DE drafted — queued for native
// review in the task file's "DE review queue".
export const ui = {
  // R-239: the topbar's force-refresh.
  refreshData: { de: 'Daten aktualisieren', en: 'Refresh data' },
  // ui/Table's floating "more rows below" button.
  scrollDown: { de: 'Weiter nach unten', en: 'Scroll down for more' },
  // R-238 §6 B2 — the app-wide critical bar. Short on purpose: it sits above every
  // screen and competes with whatever the reader came to do.
  criticalBannerCount: { de: '{count} kritischer Vorfall offen', en: '{count} critical incident(s) open' },
  criticalBannerOpen: { de: 'Öffnen', en: 'Open incidents' },
  confirm: { de: 'Bestätigen', en: 'Confirm' },
  typeToConfirm: { de: '{match} eingeben, um zu bestätigen', en: 'Type {match} to confirm' },
  sort: { de: 'Sortieren', en: 'Sort' },
  ascending: { de: 'Aufsteigend', en: 'Ascending' },
  descending: { de: 'Absteigend', en: 'Descending' },
  filter: { de: 'Filtern', en: 'Filter' },
  noData: { de: 'Keine Daten', en: 'No data' },
  searchPlaceholder: { de: 'Suchen…', en: 'Search…' },
  // G-F2: global header search results.
  searchNoResults: { de: 'Keine Treffer in geladenen Daten', en: 'No matches in loaded data' },
  // The SearchSelect's own search box.
  searchHere: { de: 'Hier suchen …', en: 'Search here…' },
  searchOptions: { de: 'Optionen durchsuchen', en: 'Search options' },
  // §27 — a form dropdown's empty state names the field: "Select Asset class".
  selectLabel: { de: '{label} auswählen', en: 'Select {label}' },
  // The ✕ that empties a SearchSelect.
  clearSelection: { de: 'Auswahl löschen', en: 'Clear selection' },
  // R-346 — the records group heading inside the ⌘K palette.
  searchRecords: { de: 'Datensätze', en: 'Records' },
  searchType_asset: { de: 'Asset', en: 'Asset' },
  searchType_incident: { de: 'Incident', en: 'Incident' },
  searchType_change: { de: 'Change', en: 'Change' },
  searchType_workOrder: { de: 'Arbeitsauftrag', en: 'Work order' },
  searchType_transferOrder: { de: 'Transfer', en: 'Transfer' },
  searchType_problem: { de: 'Problem', en: 'Problem' },
  searchType_kb: { de: 'Wissen', en: 'Knowledge' },
  searchType_requisition: { de: 'Anforderung', en: 'Requisition' },
  searchType_order: { de: 'Bestellung', en: 'Order' },
  searchType_receiving: { de: 'Wareneingang', en: 'Receiving' },
  filterNav: { de: 'Navigation filtern…', en: 'Filter navigation…' },
  toggleNav: { de: 'Navigation umschalten', en: 'Toggle navigation' },
  notifications: { de: 'Benachrichtigungen', en: 'Notifications' },
  markAllRead: { de: 'Alle als gelesen markieren', en: 'Mark all read' },
  acknowledge: { de: 'Bestätigen', en: 'Acknowledge' },
  noNotifications: { de: 'Keine Benachrichtigungen.', en: 'No notifications.' },
  seeAll: { de: 'Alle anzeigen', en: 'See all' },
  switchLanguage: { de: 'Sprache wechseln', en: 'Switch language' },
  switchToLight: { de: 'Zum Tag-Zyklus wechseln', en: 'Switch to day cycle' },
  switchToDark: { de: 'Zum Nacht-Zyklus wechseln', en: 'Switch to night cycle' },
  close: { de: 'Schließen', en: 'Close' },
  back: { de: 'Zurück', en: 'Back' },
  discardTitle: { de: 'Änderungen verwerfen?', en: 'Discard changes?' },
  discardMessage: {
    de: 'Dieses Formular enthält nicht gespeicherte Änderungen. Beim Schließen gehen sie verloren.',
    en: 'This form has unsaved changes. Closing will lose them.',
  },
  discard: { de: 'Verwerfen', en: 'Discard' },
  loading: { de: 'Lädt', en: 'Loading' },
  toggleDetails: { de: 'Details umschalten', en: 'Toggle details' },
  // R-244 — a row that OPENS a record rather than expanding it.
  openRecord: { de: 'Datensatz öffnen', en: 'Open record' },
  breadcrumb: { de: 'Brotkrümelnavigation', en: 'Breadcrumb' },
  // R-346 §21 — the compact top-bar overflow menu.
  moreOptions: { de: 'Weitere Optionen', en: 'More options' },
  site: { de: 'Standort', en: 'Site' },
  language: { de: 'Sprache', en: 'Language' },
  logOut: { de: 'Abmelden', en: 'Log out' },
  settings: { de: 'Einstellungen', en: 'Settings' },
  selectSite: { de: 'Standort wählen', en: 'Select site' },
  // R-303 — multi-account switching.
  accounts: { de: 'Konten', en: 'Accounts' },
  addAccount: { de: 'Konto hinzufügen', en: 'Add account' },
  switchAccount: { de: 'Konto wechseln', en: 'Switch account' },
  signOutOfAccount: { de: 'Von diesem Konto abmelden', en: 'Sign out of this account' },
  accountExpired: {
    de: 'Die Sitzung dieses Kontos ist abgelaufen — bitte erneut anmelden.',
    en: 'That account’s session has expired — sign in again.',
  },
  accountOffline: {
    de: 'Konto konnte nicht gewechselt werden — keine Verbindung. Sitzung bleibt bestehen.',
    en: 'Could not switch — no connection. The session is still there.',
  },
  cancelAddAccount: { de: 'Abbrechen — zurück zur Konsole', en: 'Cancel — back to the console' },
  signOutConfirmTitle: { de: 'Von diesem Konto abmelden?', en: 'Sign out of this account?' },
  signOutConfirmMessage: {
    de: '{email} wird von diesem Gerät abgemeldet. Für den erneuten Zugriff ist eine vollständige Anmeldung samt Bestätigungscode nötig.',
    en: '{email} will be signed out on this device. Getting back in needs a full sign-in, including a verification code.',
  },
  signOutConfirmLabel: { de: 'Abmelden', en: 'Sign out' },
  // R-346 — the ⌘K command palette. Navigation only: it jumps to PAGES, while the
  // topbar's global search finds RECORDS. The copy keeps the two apart on purpose.
  commandPaletteTitle: { de: 'Zu Seite springen', en: 'Jump to page' },
  commandPalettePlaceholder: { de: 'Seite suchen…', en: 'Search pages…' },
  commandPaletteEmpty: { de: 'Keine Seite passt zu „{query}“.', en: 'No page matches “{query}”.' },
  commandPaletteHint: {
    de: '↑↓ wählen · ↵ öffnen · Esc schließen',
    en: '↑↓ to move · ↵ to open · Esc to close',
  },
  commandPaletteOpen: { de: 'Zu Seite springen', en: 'Jump to page' },

  // ui/Table `ledger` — the scrolling list with a position/progress footer.
  ledgerRows: { de: 'Zeilen', en: 'Rows' },
  ledgerLoaded: { de: '{loaded} von {total} geladen', en: '{loaded} of {total} loaded' },
  ledgerStatusIdle: { de: 'Lädt beim Scrollen nach', en: 'Loads more as you scroll' },
  ledgerStatusPaused: { de: 'Automatisches Nachladen pausiert', en: 'Auto-load paused' },
  ledgerStatusDone: { de: 'Alle Einträge geladen', en: 'All entries loaded' },
  ledgerStatusFetching: { de: 'Lädt weitere …', en: 'Loading more…' },
  ledgerStatusLimited: { de: 'Server-Limit erreicht', en: 'Server limit reached' },
  ledgerStatusError: { de: 'Laden fehlgeschlagen', en: 'Load failed' },
  ledgerStatusLoading: { de: 'Wird geladen …', en: 'Loading…' },
  ledgerBackToNewest: { de: 'Zum Neuesten', en: 'Back to newest' },
  ledgerNew_one: { de: '{count} neuer Eintrag', en: '{count} new entry' },
  ledgerNew_many: { de: '{count} neue Einträge', en: '{count} new entries' },
  ledgerPaused: {
    de: 'Automatisches Nachladen bei {loaded} Einträgen pausiert · {remaining} ältere Einträge nicht geladen',
    en: 'Auto-load paused at {loaded} entries · {remaining} older entries not loaded',
  },
  ledgerPausedMore: {
    de: 'Automatisches Nachladen bei {loaded} Einträgen pausiert · weitere ältere Einträge nicht geladen',
    en: 'Auto-load paused at {loaded} entries · older entries not loaded',
  },
  ledgerFetching: { de: '{count} weitere werden geladen …', en: 'Loading {count} more…' },
  ledgerLoadMore: { de: '{count} weitere laden', en: 'Load {count} more' },
  ledgerEnd: { de: 'Ende der Liste', en: 'End of list' },
  ledgerEndNote: { de: 'Alle {total} Einträge geladen', en: 'All {total} entries loaded' },
  ledgerEndNoteSince: {
    de: '{total} Einträge · ältester {earliest}',
    en: '{total} entries · earliest {earliest}',
  },
  ledgerLimitedTitle: { de: 'Server-Limit', en: 'Server limit' },
  ledgerLimitedNote: {
    de: 'Der Server liefert höchstens {total} Einträge auf einmal — es kann weitere geben.',
    en: 'The server sends at most {total} entries at once — there may be more.',
  },
  ledgerError: { de: 'Die Einträge konnten nicht geladen werden.', en: 'Couldn’t load the entries.' },
  ledgerErrorStale: {
    de: 'Aktualisieren fehlgeschlagen — die {count} Zeilen oben sind möglicherweise veraltet.',
    en: 'Couldn’t refresh — the {count} rows above may be out of date.',
  },
  ledgerRetry: { de: 'Erneut versuchen', en: 'Retry' },
} as const satisfies Namespace;
